import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { differenceInCalendarDays, format } from 'date-fns';
import { useRouter } from 'next/router';
import * as S from '@/components/Ad/Settings/Settings.styles';
import { Button } from '@/components/UI/Button/Button';
import { ContactsIcon, QuestionIcon } from '@/components/UI/Icons/Icons';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { Popup } from '@/components/UI/Popup/Popup';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import {
  EditPopupType,
  RemoveAdPayload,
} from '@/components/Ad/Settings/Settings.types';
import { editPopupData } from '@/components/Ad/Settings/Settings.constants';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { DESTROY_AD } from '@/api/mutations/ads.mutations';
import { AdStatus } from '@/types';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { Divider } from '@/components/UI/Divider/Divider.styles';
import { DeleteAdSheet } from '@/components/Ad/DeleteAdSheet';
import { EDIT_AD_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import { useServerDataContext } from '@/contexts';
import { GetAdHasEditPendingApprovalResponse } from '@/api/types/ads.types';
import { GET_AD_HAS_EDIT_PENDING_APPROVAL } from '@/api/queries/ads.queries';

export const Settings = () => {
  const { t } = useTranslation();
  const { adDetails, refetch: refetchAdDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const {
    status,
    editLockedUntil,
    hits,
    id,
    expiresAt,
    callsCount,
    bookmarksCount,
    deleteReasons,
  } = adData ?? {};
  const customPush = useCustomPush();
  const {
    query: { slug },
    replace,
  } = useRouter();
  const legacyId = slug?.[2];
  const [isContactsOpen, toggleContacts] = useToggleOpenState();
  const [isEditOpen, toggleEdit] = useToggleOpenState();
  const [isRemoveOpen, toggleRemove] = useToggleOpenState(false);
  const [isEditPendingOpen, toggleEditPending] = useToggleOpenState();
  const [isDestroying, setIsDestroying] = useState(false);
  const { url, hash } = useUrlAndHashFromPath() as {
    url: string;
    hash: EditPopupType;
  };
  const canEdit = status === AdStatus.Published;
  const canRemove = status
    ? [AdStatus.Pending, AdStatus.Published, AdStatus.AwaitingPayment].includes(
        status
      )
    : false;

  function closeEditPopup() {
    toggleEdit(false);
    replace(url, undefined, { scroll: false });
  }

  const [destroyAd] = useMutation(DESTROY_AD, {
    onCompleted: () => {
      refetchAdDetails().then(() => setIsDestroying(false));
    },
  });

  const [
    checkHasEditPendingApproval,
    { loading: isEditPendingApprovalLoading },
  ] = useLazyQuery<GetAdHasEditPendingApprovalResponse>(
    GET_AD_HAS_EDIT_PENDING_APPROVAL,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    }
  );

  async function edit() {
    if (isEditPendingApprovalLoading) return;

    const { data } = await checkHasEditPendingApproval({
      variables: { legacyId },
    });

    const hasEditPendingApproval = data?.ad?.hasEditPendingApproval ?? false;

    if (hasEditPendingApproval) {
      toggleEditPending(true);
      return;
    }

    if (editLockedUntil) {
      replace(`${url}/#${EditPopupType.Error}`, undefined, {
        scroll: false,
      });
      return;
    }

    customPush(`${url}/${EDIT_AD_PAGE_SLUG}`);
  }

  async function remove(data: RemoveAdPayload) {
    if (!canRemove || isDestroying) return;

    setIsDestroying(true);
    await destroyAd({
      variables: {
        adId: id,
        deleteReasonId: data.id,
        deleteReasonDescription: data?.delete_reason_description ?? '',
      },
    });
    toggleRemove(false);
  }

  useEffect(() => {
    if (hash === EditPopupType.Error || hash === EditPopupType.OnReview) {
      toggleEdit(true);
    }
  }, [hash]);

  if (!adData) return null;

  const daysToDeactivation =
    expiresAt && differenceInCalendarDays(new Date(expiresAt), new Date());
  const editDateTime = editLockedUntil
    ? format(new Date(editLockedUntil), 'dd.MM.yyyy HH:mm')
    : undefined;
  const editPopupTexts = editPopupData(t, editDateTime)[hash];
  const { title: editPopupTitle, description: editPopupDescription } =
    editPopupTexts ?? {};

  return (
    <>
      <S.Table>
        <S.Item data-testid={AdPageTestIds.settings.views}>
          <S.Label $variant="1" data-testid={AdPageTestIds.settings.label}>
            {t('pages.ad.views')}
          </S.Label>
          <Typography.Header
            $variant="2"
            data-testid={AdPageTestIds.settings.value}
          >
            {hits}
          </Typography.Header>
        </S.Item>

        <S.Item data-testid={AdPageTestIds.settings.contacts.self}>
          <S.Label
            $variant="1"
            onClick={toggleContacts}
            data-testid={AdPageTestIds.settings.label}
          >
            {t('pages.ad.contacts')}
            <QuestionIcon />
          </S.Label>
          <Typography.Header
            $variant="2"
            data-testid={AdPageTestIds.settings.value}
          >
            {callsCount}
          </Typography.Header>
        </S.Item>

        <S.Item data-testid={AdPageTestIds.settings.inFavorites}>
          <S.Label $variant="1" data-testid={AdPageTestIds.settings.label}>
            {t('pages.ad.in_favorites')}
          </S.Label>
          <Typography.Header
            $variant="2"
            data-testid={AdPageTestIds.settings.value}
          >
            {bookmarksCount}
          </Typography.Header>
        </S.Item>
      </S.Table>

      {expiresAt && status !== AdStatus.Expired && (
        <S.DeactivationInfo
          $variant="1"
          data-testid={AdPageTestIds.settings.daysToDeactivation}
        >
          {t('pages.ad.days_to_deactivation', { days: daysToDeactivation })}
        </S.DeactivationInfo>
      )}

      {(canEdit || canRemove) && (
        <S.Actions>
          {canEdit && (
            <Button
              appearance="secondaryOutlined"
              isFullWidth
              onClick={edit}
              data-testid={AdPageTestIds.settings.editButton}
              disabled={isEditPendingApprovalLoading}
            >
              {t('common.edit')}
            </Button>
          )}
          {canRemove && (
            <Button
              appearance="primaryOutlined"
              isFullWidth
              onClick={toggleRemove}
              data-testid={AdPageTestIds.settings.remove.button}
            >
              {t('pages.ad.remove_ad')}
            </Button>
          )}

          {canRemove && (
            <DeleteAdSheet
              submit={remove}
              toggle={toggleRemove}
              isOpen={isRemoveOpen}
              reasons={deleteReasons ?? []}
            />
          )}
        </S.Actions>
      )}

      {editPopupTexts && (
        <Popup
          dataTestId={AdPageTestIds.settings.editPopup}
          isOpen={isEditOpen}
          close={closeEditPopup}
          title={editPopupTitle}
          description={editPopupDescription}
          actions={{ left: { text: t('common.ok'), onClick: closeEditPopup } }}
          {...(hash === EditPopupType.OnReview && {
            icon: 'check-circle-rounded.svg',
            iconSize: 35,
          })}
        />
      )}

      {isEditPendingOpen && (
        <Popup
          dataTestId={AdPageTestIds.settings.editPendingPopup}
          isOpen={isEditPendingOpen}
          close={toggleEditPending}
          title={t('pages.ad.edit_pending_approval')}
          description={t('pages.ad.edit_pending_approval_description')}
          actions={{
            left: { text: t('common.ok'), onClick: toggleEditPending },
          }}
        />
      )}

      <BottomSheet
        isOpen={isContactsOpen}
        close={toggleContacts}
        title={t('pages.ad.contacts')}
        dataTestId={AdPageTestIds.settings.contacts.bottomSheet.self}
      >
        <S.Contacts>
          <ContactsIcon />
          <S.ContactsText
            $variant="2"
            data-testid={AdPageTestIds.settings.contacts.bottomSheet.title}
          >
            {t('pages.ad.contacts_tip')}
          </S.ContactsText>
          <Button
            appearance="secondary"
            isFullWidth
            onClick={toggleContacts}
            data-testid={AdPageTestIds.settings.contacts.bottomSheet.button}
          >
            {t('common.got_it')}
          </Button>
        </S.Contacts>
      </BottomSheet>

      <Divider $isSmall />
    </>
  );
};
